import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

// Privacy policy always reads from PROD DB regardless of environment,
// since it's public content and only maintained in production.
const LABELS_DB_URL =
  process.env.REACT_APP_FIREBASE_PROD_DATABASE_URL ||
  process.env.REACT_APP_FIREBASE_DATABASE_URL;

const appColors = {
  primary: '#81a550',
  secondary: '#66AA44',
  accent: '#e2c269',
  background: '#F5E4BA',
  textPrimary: '#2D2D2D',
  textSecondary: '#6B6B6B',
  separatorBrown: '#D4B896',
};

const PrivacyPage = () => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await fetch(`${LABELS_DB_URL}/labels/privacy_policy/en.json`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setPolicy(data);
      } catch (err) {
        console.error('Failed to load privacy policy:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, []);

  const renderSection = (title, content) => (
    <div key={title} style={{ marginBottom: '28px' }}>
      <h2 style={{
        fontSize: '17px',
        fontWeight: '700',
        color: appColors.textPrimary,
        marginBottom: '10px',
        paddingBottom: '8px',
        borderBottom: `2px solid ${appColors.separatorBrown}`,
        margin: '0 0 10px',
      }}>
        {title}
      </h2>
      <p style={{
        fontSize: '14px',
        lineHeight: '1.8',
        color: appColors.textSecondary,
        margin: 0,
        whiteSpace: 'pre-wrap',
      }}>
        {content}
      </p>
    </div>
  );

  const renderPolicyContent = () => {
    if (!policy || typeof policy !== 'object') return null;

    // Build sections from privacy_*_title + privacy_*_content pairs.
    const sectionMap = {};
    Object.entries(policy).forEach(([key, value]) => {
      const titleMatch = key.match(/^privacy_(.+)_title$/);
      const contentMatch = key.match(/^privacy_(.+)_content$/);
      if (titleMatch) {
        const slug = titleMatch[1];
        sectionMap[slug] = sectionMap[slug] || {};
        sectionMap[slug].title = value;
      } else if (contentMatch) {
        const slug = contentMatch[1];
        sectionMap[slug] = sectionMap[slug] || {};
        sectionMap[slug].content = value;
      }
    });

    const sections = Object.values(sectionMap).filter((s) => s.title && s.content);
    if (sections.length === 0) return null;
    return sections.map((section) => renderSection(section.title, section.content));
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${appColors.background} 0%, #f8f4e6 100%)`,
      backgroundAttachment: 'fixed',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    }}>
      {/* Background decorations — same as LoginPage */}
      <div style={{
        position: 'fixed',
        top: '-20%',
        right: '-10%',
        width: '500px',
        height: '500px',
        background: `radial-gradient(circle, ${appColors.primary}20 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '-15%',
        width: '600px',
        height: '600px',
        background: `radial-gradient(circle, ${appColors.accent}30 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      {/* Main content */}
      <main style={{
        flex: 1,
        position: 'relative',
        zIndex: 1,
        maxWidth: '760px',
        width: '100%',
        margin: '0 auto',
        padding: '40px 24px 60px',
      }}>
        {/* Logo row with X close */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <img
            src="/mathzoo-logo.png"
            alt="MathZoo Junior"
            style={{ height: '80px', marginLeft: '-12px' }}
          />
          <a
            href="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.08)',
              color: appColors.textSecondary,
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            <X size={18} />
          </a>
        </div>

        {/* Page title */}
        <h1 style={{
          fontSize: '26px',
          fontWeight: '800',
          color: appColors.textPrimary,
          margin: '0 0 24px',
          textAlign: 'center',
        }}>
          Privacy Policy
        </h1>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
          padding: '36px 40px',
        }}>
          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: appColors.textSecondary }}>
              <div style={{
                width: '36px',
                height: '36px',
                border: `3px solid ${appColors.separatorBrown}`,
                borderTopColor: appColors.primary,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ margin: 0, fontSize: '14px' }}>Loading privacy policy...</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div style={{
              padding: '16px',
              background: '#FEF3C7',
              borderRadius: '12px',
              border: '1px solid #F59E0B',
              color: appColors.textSecondary,
              fontSize: '14px',
              textAlign: 'center',
            }}>
              Unable to load the policy content. Please try again later.
            </div>
          )}

          {/* No content */}
          {!loading && !error && !policy && (
            <p style={{ fontSize: '14px', color: appColors.textSecondary, textAlign: 'center', padding: '40px 0', margin: 0 }}>
              Privacy policy content is not available yet.
            </p>
          )}

          {/* Policy sections */}
          {!loading && !error && policy && renderPolicyContent()}
        </div>
      </main>

      {/* Footer */}
      <footer style={{ padding: '12px 48px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <p style={{ fontSize: '12px', color: appColors.textSecondary, margin: 0 }}>
          &copy; 2026 MathZoo Junior. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPage;
